"use client";

import { Form, Field as FormischField, type SubmitHandler, setInput, submit, useForm } from "@formisch/react";
import { Field, FieldLabel } from "@repo/design-system-2/components/ui/field";
import { Input } from "@repo/design-system-2/components/ui/input";
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectLabel,
    SelectSeparator,
    SelectTrigger,
    SelectValue,
} from "@repo/design-system-2/components/ui/select";
import { Switch } from "@repo/design-system-2/components/ui/switch";
import { cn } from "@repo/design-system-2/lib/utils";
import { Fragment, useEffect, useMemo, useRef } from "react";
import type { InferOutput } from "valibot";
import { sameStringArray } from "@/lib/helpers/filter-params";
import { regionList } from "./regions";
import { feisFiltersSchema } from "./validation";

export type FeisFilters = InferOutput<typeof feisFiltersSchema>;

const sortItems = [
    { label: "Date", value: "date" },
    { label: "Series name", value: "series" },
];

type FeisFilterFormProps = {
    id: string;
    initialFilters: FeisFilters;
    layout: "column" | "row";
    onFiltersChange: (filters: FeisFilters) => void;
    showExam?: boolean;
    showSort?: boolean;
    showSearch?: boolean;
    showQualifier?: boolean;
};

export function FeisFilterForm({ id, initialFilters, layout, onFiltersChange, showExam = false, showSort = true, showSearch = true, showQualifier = true }: FeisFilterFormProps) {
    const form = useForm({
        initialInput: initialFilters,
        schema: feisFiltersSchema,
    });

    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(
        () => () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        },
        []
    );

    function submitDebounced() {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(() => {
            submit(form);
        }, 400);
    }

    const handleSubmit: SubmitHandler<typeof feisFiltersSchema> = (output) => {
        onFiltersChange(output);
    };

    const regionItems = useMemo(
        () => regionList.flatMap((section) => section.items.map((item) => ({ label: item.label, value: item.id }))),
        []
    );
    const regionLabels = useMemo(() => new Map(regionItems.map((item) => [item.value, item.label])), [regionItems]);

    function regionTriggerLabel(value: string[] | null) {
        if (!value || value.length === 0) {
            return "All regions";
        }
        if (value.length === 1) {
            return regionLabels.get(value[0]) ?? "1 region";
        }
        return `${value.length} regions`;
    }

    return (
        <Form
            className={cn(layout === "row" ? "flex flex-row flex-wrap items-end gap-4" : "flex flex-col gap-6")}
            id={id}
            of={form}
            onSubmit={handleSubmit}
        >
            {showSearch &&
                <FormischField of={form} path={["search"]}>
                    {(field) => (
                        <Field className={cn(layout === "row" && "w-64")}>
                            <FieldLabel htmlFor={`${id}-search`}>Search</FieldLabel>
                            <Input
                                {...field.props}
                                id={`${id}-search`}
                                onChange={(event) => {
                                    field.props.onChange(event);
                                    submitDebounced();
                                }}
                                placeholder="Search feiseanna..."
                                type="search"
                                value={field.input ?? ""}
                            />
                        </Field>
                    )}
                </FormischField>}

            <FormischField of={form} path={["regionIds"]}>
                {(field) => (
                    <Field className={cn(layout === "row" && "w-48")}>
                        <FieldLabel htmlFor={`${id}-region`}>Regions</FieldLabel>
                        <Select
                            items={regionItems}
                            multiple
                            onValueChange={(value) => {
                                const nextValue = (value as string[] | null) ?? [];
                                const currentValue = field.input ?? [];
                                if (sameStringArray(nextValue, currentValue)) {
                                    return;
                                }

                                setInput(form, { input: nextValue, path: ["regionIds"] });
                                submit(form);
                            }}
                            value={field.input ?? []}
                        >
                            <SelectTrigger className="w-full" id={`${id}-region`}>
                                <SelectValue>{regionTriggerLabel}</SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                                {regionList.map((s, index) => (
                                    <Fragment key={s.label}>
                                        <SelectGroup>
                                            <SelectLabel>{s.label}</SelectLabel>
                                            {s.items.map((i) => (
                                                <SelectItem key={i.id} value={i.id}>
                                                    {i.label}
                                                </SelectItem>
                                            ))}
                                        </SelectGroup>
                                        {index < regionList.length - 1 && <SelectSeparator />}
                                    </Fragment>
                                ))}
                            </SelectContent>
                        </Select>
                    </Field>
                )}
            </FormischField>

            {showSort && (
                <FormischField of={form} path={["sort"]}>
                    {(field) => (
                        <Field className={cn(layout === "row" && "w-40")}>
                            <FieldLabel htmlFor={`${id}-sort`}>Sort by</FieldLabel>
                            <Select
                                items={sortItems}
                                onValueChange={(value) => {
                                    const nextValue = (value as FeisFilters["sort"] | null) ?? "date";
                                    if (nextValue === (field.input ?? "date")) {
                                        return;
                                    }

                                    setInput(form, {
                                        input: nextValue,
                                        path: ["sort"],
                                    });
                                    submit(form);
                                }}
                                value={field.input ?? "date"}
                            >
                                <SelectTrigger className="w-full" id={`${id}-sort`}>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {sortItems.map((item) => (
                                        <SelectItem key={item.value} value={item.value}>
                                            {item.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </Field>
                    )}
                </FormischField>
            )}

            {showExam && (
            <FormischField of={form} path={["hasExam"]}>
                {(field) => (
                    <Field className={cn(layout === "row" && "w-fit pb-2")} orientation="horizontal">
                        <Switch
                            checked={field.input ?? false}
                            id={`${id}-has-exam`}
                            onCheckedChange={(checked) => {
                                if (checked === (field.input ?? false)) {
                                    return;
                                }

                                setInput(form, { input: checked, path: ["hasExam"] });
                                submit(form);
                            }}
                        />
                        <FieldLabel className="font-normal" htmlFor={`${id}-has-exam`}>
                            Grade exam
                        </FieldLabel>
                    </Field>
                )}
            </FormischField>
            )}

            <FormischField of={form} path={["registrationOpen"]}>
                {(field) => (
                    <Field className={cn(layout === "row" && "w-fit pb-2")} orientation="horizontal">
                        <Switch
                            checked={field.input ?? false}
                            id={`${id}-registration-open`}
                            onCheckedChange={(checked) => {
                                if (checked === (field.input ?? false)) {
                                    return;
                                }

                                setInput(form, { input: checked, path: ["registrationOpen"] });
                                submit(form);
                            }}
                        />
                        <FieldLabel className="font-normal" htmlFor={`${id}-registration-open`}>
                            Registration open
                        </FieldLabel>
                    </Field>
                )}
            </FormischField>

            {showQualifier &&
                <FormischField of={form} path={["worldQualifier"]}>
                    {(field) => (
                        <Field className={cn(layout === "row" && "w-fit pb-2")} orientation="horizontal">
                            <Switch
                                checked={field.input ?? false}
                                id={`${id}-world-qualifier`}
                                onCheckedChange={(checked) => {
                                    if (checked === (field.input ?? false)) {
                                        return;
                                    }

                                    setInput(form, { input: checked, path: ["worldQualifier"] });
                                    submit(form);
                                }}
                            />
                            <FieldLabel className="font-normal" htmlFor={`${id}-world-qualifier`}>
                                World qualifier
                            </FieldLabel>
                        </Field>
                    )}
                </FormischField>
            }
        </Form>
    );
}
